import React from 'react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
} from '@components/ui/accordion';
import { Table, TableBody, TableCell, TableRow } from '@components/ui/table';
import { WorkoutGpxAnalysisSummaryDto } from '../../core/api/FitnessActivityClient';
import { GpxAnalysisChip } from './GpxAnalysisChip';

interface GpxAnalysisSegment {
    label?: string;
    pathMeters?: number;
    maxSpeedKmh?: number;
    durationSeconds?: number;
    reasons?: string[];
}

function formatValue(value: unknown): string {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') {
        return Number.isInteger(value) ? String(value) : value.toFixed(2);
    }
    return String(value);
}

function formatKey(key: string): string {
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase())
        .trim();
}

interface GpxAnalysisPanelProps {
    analysis: WorkoutGpxAnalysisSummaryDto;
}

export function GpxAnalysisPanel({ analysis }: GpxAnalysisPanelProps) {
    const result = analysis.result ?? {};
    const reason = result.reason as string | undefined;
    const segments =
        (result.segments as GpxAnalysisSegment[] | undefined) ?? [];
    const scalars = Object.entries(result).filter(
        ([key]) => key !== 'segments' && key !== 'reason'
    );

    return (
        <Accordion type="single" collapsible className="rounded-lg border px-4">
            <AccordionItem value="gpx-analysis">
                <AccordionTrigger className="hover:no-underline">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-sm font-medium">
                            GPX Analysis
                        </span>
                        <GpxAnalysisChip
                            action={analysis.action}
                            reason={reason}
                        />
                        <span className="text-sm text-muted-foreground">
                            {reason ?? ''}
                        </span>
                    </div>
                </AccordionTrigger>
                <AccordionContent>
                    <p className="mb-2 text-xs text-muted-foreground">
                        Shadow mode — this verdict was recorded, not acted on.
                        The workout was accepted or rejected by the existing
                        rules.
                    </p>
                    <Table>
                        <TableBody>
                            {scalars.map(([key, value]) => (
                                <TableRow key={key}>
                                    <TableCell className="w-[40%] text-muted-foreground">
                                        {formatKey(key)}
                                    </TableCell>
                                    <TableCell>{formatValue(value)}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell className="text-muted-foreground">
                                    Analysed At
                                </TableCell>
                                <TableCell>{analysis.analysedAt}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    {segments.length > 0 && (
                        <>
                            <p className="mt-4 mb-2 text-sm font-medium">
                                Anomaly segments ({segments.length})
                            </p>
                            <Table>
                                <TableBody>
                                    {segments.map((segment, index) => (
                                        <TableRow key={index}>
                                            <TableCell className="w-[40%] align-top">
                                                <p className="text-sm">
                                                    {segment.label ?? 'UNCLEAR'}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    {formatValue(
                                                        segment.pathMeters
                                                    )}{' '}
                                                    m ·{' '}
                                                    {formatValue(
                                                        segment.maxSpeedKmh
                                                    )}{' '}
                                                    km/h ·{' '}
                                                    {formatValue(
                                                        segment.durationSeconds
                                                    )}{' '}
                                                    s
                                                </p>
                                            </TableCell>
                                            <TableCell className="align-top">
                                                {(segment.reasons ?? []).map(
                                                    (r, i) => (
                                                        <p
                                                            key={i}
                                                            className="text-xs text-muted-foreground"
                                                        >
                                                            {r}
                                                        </p>
                                                    )
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </>
                    )}
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}
